<!-- ⚠️ NOT A PROGRESS BAR. Every step here is a place the advisor can be, and most of them
     are reachable at any time -- the early steps are optional, so someone can open the wizard and
     go straight to the step they came for. The indicator has to look like that.

     ⚠️ Which is why the BUBBLES CARRY NO NUMBERS any more. "1 2 3 4 5" states a fixed order and
     a position within it, so a numbered row reads as a sequence to be worked through and the
     later numbers read as "not yet" rather than "not visited". The glyphs now describe STATE
     instead of position: a check for a step that has been through, a filled dot for where the
     advisor is, a hollow dot for one they have not opened. Nothing about that says which order
     to do them in.

     Kept: the connecting hairline, in ONE flat grey for every segment. It groups the row into a
     single control, and because it never fills in behind the current step it does not read as a
     bar being completed. -->
<div class="flex items-center px-20 py-10 border-b border-gray-lighter">
  @for (label of steps(); track label; let i = $index, last = $last) {
    <div class="flex items-center gap-x-5 text-sm whitespace-nowrap"
      [ngClass]="labelClasses(i + 1)"
      [attr.role]="isActionable(i + 1) ? 'button' : null"
      [attr.tabindex]="isActionable(i + 1) ? 0 : null"
      [attr.title]="isLocked(i + 1)
        ? label + ' is not available while this account holds a separately managed account'
        : isNavigable(i + 1) ? 'Go to ' + label : null"
      [attr.aria-disabled]="isLocked(i + 1) ? 'true' : null"
      [attr.aria-current]="i + 1 === current() ? 'step' : null"
      [class.opacity-50]="isLocked(i + 1)"
      (click)="select(i + 1)"
      (keydown.enter)="select(i + 1)"
      (keydown.space)="select(i + 1); $event.preventDefault()">
      <!-- ⚠️ Same 2rem circle in all three states, filled or not. Shrinking the unvisited ones
           into small dots was the other way to lose the numbers, and it makes the row read as a
           timeline with milestones of different importance -- which is the sequence reading
           again, in a different costume. Identical circles say "these are the same kind of
           thing, pick one".

           ⚠️ `flex-shrink-0`, and it is NOT optional. This is a flex item in a row of five
           steps whose labels are `whitespace-nowrap`, so the labels cannot give ground and the
           circles are the only thing left that can: without it they compress horizontally and
           `rounded-full` renders them as OVALS. It only shows below whatever width the row stops
           fitting at, which is why it survived review -- 2rem square is what the classes say and
           what a wide window draws. -->
      <!-- ⚠️ The CURRENT step has no CSS border: its ring is drawn inside the SVG below, with
           the dot, so the two cannot come apart. Every other state keeps the border, because
           nothing has to line up inside them. -->
      <span class="flex items-center justify-center w-20 h-20 rounded-full flex-shrink-0"
        [ngClass]="bubbleClasses(i + 1)">
        @if (i + 1 < current()) {
          <!-- BEEN THROUGH. A check, not a tick-box: it reports that the step has been seen, and
               says nothing about whether the advisor entered anything there. Most of these steps
               are legitimately left empty. -->
          <i class="material-icons" style="font-size: 1.4rem">check</i>
        } @else if (i + 1 === current()) {
          <!-- WHERE THEY ARE. A mint RING with a mint dot in it -- the shape of a selected
               radio, which is exactly the meaning: one of a set, and this is the one.

               ⚠️ NOT a filled mint disc with a white dot, which is what it was. Solid, it was
               the heaviest thing in the row and read as a status badge rather than as one option
               of five selected -- and a white dot on a filled ground is the shape of a
               *deselected* radio inverted, which is the wrong half of the metaphor. Ring plus
               dot is how a chosen radio has always looked, and it keeps the current step the
               same visual weight as the steps either side of it, which is the point of the whole
               row: these are all places the advisor can be.

               ⚠️ ONE SVG DRAWING BOTH, and that is the whole point of it being an SVG. This has
               now been three things, and the first two were the same mistake twice:

                 nested <span>   a sized box centred by flexbox   -- visibly off-centre
                 material icon   a glyph centred by font metrics  -- off-centre, and it MOVED
                                                                     between screen sizes
                 this            ring and dot in one coordinate
                                 space, concentric by construction

               Both earlier versions asked the browser to line up two separate boxes: a 2rem
               circle whose border eats into its own content box, and a child centred within what
               is left. Whether that lands on a whole pixel depends on how 2rem and the 2px
               border resolve at the current zoom and device pixel ratio, and the parities change
               from one screen to the next -- which is exactly the "fine here, off there" that
               kept coming back. A font glyph adds its own side bearings on top.

               Inside one viewBox there is no lining up to do: both circles share cx/cy, the
               browser scales the whole thing, and rounding can shift the glyph as a unit but can
               never decentre the dot within the ring.

               ⚠️ Sized to MATCH the other two states rather than to any absolute: stroke 2 of 32
               is the same proportion as `border-2` on a 2rem circle, so the current ring and the
               rings either side of it are the same weight. The dot is r=5, close to what the
               Material glyph drew at 1rem, and deliberately well inside the ring -- a larger one
               reads as a filled disc again, by another route.

               `currentColor` twice, so the colour keeps coming from `text-mint-darkest` on the
               parent and there is no second place to change it. -->
          <svg viewBox="0 0 32 32" class="w-20 h-20" aria-hidden="true" focusable="false">
            <circle cx="16" cy="16" r="15" fill="none" stroke="currentColor" stroke-width="2" />
            <circle cx="16" cy="16" r="5" fill="currentColor" />
          </svg>
        } @else {
          <!-- NOT OPENED YET: an empty ring, and nothing in it.

               ⚠️ It briefly had a grey pip inside, on the reasoning that an empty circle would
               read as a disabled control. That was wrong, and wrong in a way worth recording: a
               FILLED CENTRE MEANS SELECTED. Four steps carrying one made the row read as a radio
               group with everything selected and then greyed out -- so the one state that is
               actually selected had nothing left to distinguish it, and the whole row looked
               broken rather than merely quiet.

               Empty ring against filled ring is how a radio group has always told those two
               apart, and it is legible precisely because nothing else in the row is competing.
               The reachability signal is carried by the hover underline and the pointer, which
               is where it belongs -- the glyph says what the step IS, not whether it is
               clickable. -->
        }
      </span>
      <span>{{ label }}</span>
    </div>
    @if (!last) {
      <div class="flex-1 h-px mx-8 bg-gray-lighter"></div>
    }
  }
</div>
